import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ITimelineEntryWithData } from "@app/directives/_veri/v-timeline/v-timeline.component";
import { DateService } from "@app/services/date.service";
import { TranslationService } from "@app/services/translation.service";
import { UnitConversionService, UnitConversionType } from "@app/services/unitConversion.service";
import { SortDirection } from "@enums/SortDirection";
import { VeriDate } from "@services/veriDate.service";
import { ITemperatureSetpointWithHistories } from "@temperature/_components/tempGraph/temperature-graph.component";
import { ITemperatureRecord } from "@temperature/_models/temperature/ITemperatureRecord";
import { ITemperatureSetpoint } from "@temperature/_models/temperature/ITemperatureSetpoint";
import { ITemperatureSetpointHistory } from "@temperature/_models/temperature/ITemperatureSetpointHistory";
import { ITemperatureSetpointHistoryQueryOptions } from "@temperature/_models/temperature/ITemperatureSetpointHistoryQueryOptions";
import { ITemperatureSetpointQueryOptions } from "@temperature/_models/temperature/ITemperatureSetpointQueryOptions";
import { lastValueFrom, Observable } from "rxjs";
import { IZoneWithProbes } from "../_models/temperature/ITemperatureZone";

@Injectable({
    providedIn: 'root'
})
export class TemperatureSetpointService {
    constructor(
        private readonly http: HttpClient,
        private readonly veriDate: VeriDate,
        private readonly dateService: DateService,
        private readonly translationService: TranslationService,
        private readonly unitConversionService: UnitConversionService) {
        
    }

    getTemperatureSetpoints(queryOptions: ITemperatureSetpointQueryOptions = {}): Observable<ITemperatureSetpoint[]> {
        return this.http.get<ITemperatureSetpoint[]>('/api/setpoints', { params: queryOptions as HttpParams });
    }

    getTemperatureSetpointHistories(queryOptions: ITemperatureSetpointHistoryQueryOptions = {}) {
        return this.http.get<ITemperatureSetpointHistory[]>(`/api/setpoints/history`, { params: queryOptions as HttpParams })
    }

    loadSetpoints(vehicleId: number, start: Date | string, end: Date | string): Promise<ITemperatureSetpointWithHistories[]> {
        const setpointPromises: Promise<any>[] = [];
        setpointPromises.push(lastValueFrom(this.getTemperatureSetpoints({ VehicleId: vehicleId })));
        setpointPromises.push(lastValueFrom(this.getTemperatureSetpointHistories({ VehicleId: vehicleId, Start: start, End: end })));


        return Promise.all(setpointPromises).then(([setpoints, setpointHistories]: [ITemperatureSetpointWithHistories[], ITemperatureSetpointHistory[]]) => {
            setpointHistories.sort((a, b) => {
                return this.veriDate.sort(a.StartTime, b.StartTime, SortDirection.Ascending);
            });

            setpoints.forEach(setpoint => {
                setpoint.histories = setpointHistories.filter(h => h.TemperatureSetpointId == setpoint.Id)
            });
            return setpoints;
        });

    }

    getSetpointForTemperatureRecord(record: ITemperatureRecord, temperatureSetpoints: ITemperatureSetpointWithHistories[], zoneId: number): ITemperatureSetpointHistory {
        if (!temperatureSetpoints || !record) {
            return;
        }
        const setpoint = temperatureSetpoints.find(setpoint => {
            return setpoint.ZoneId == zoneId;
        });
        const setpointsAtTime = setpoint.histories.filter(setpoint => {
            return new Date(setpoint.StartTime).getTime() < new Date(record.Timestamp).getTime();
        });
        setpointsAtTime.sort((a, b) => {
            return new Date(b.StartTime).getTime() - new Date(a.StartTime).getTime();
        })

        return setpointsAtTime[0];
    }

    mapHistoriesToSetPoints(temperatureSetpoints: ITemperatureSetpointWithHistories[], temperatureSetpointHistories: ITemperatureSetpointHistory[]) {
        temperatureSetpointHistories.sort((a, b) => {
            return new Date(b.StartTime).getTime() - new Date(a.StartTime).getTime();
        })

        temperatureSetpoints.forEach(setpoint => {
            setpoint.histories = temperatureSetpointHistories.filter(history => history.TemperatureSetpointId === setpoint.Id)
        });
    }

    createTimelineEntriesForSetpointChanges(zones: IZoneWithProbes[], historyStartTime: Date): ITimelineEntryWithData<number>[] {
        const timelineEntries: ITimelineEntryWithData<number>[] = [];

        zones.forEach(zone => {
            const recentHistories = zone.setpoint?.histories?.filter(history => {
                return this.dateService.date1IsAfterDate2(history.StartTime, historyStartTime);
            });

            if (!recentHistories || !recentHistories.length) {
                return timelineEntries;
            }


            recentHistories.forEach(history => {
                timelineEntries.push({
                    icon: "device_thermostat",
                    date: history.StartTime as any,
                    largeDetails: {
                        title: this.buildSetpointChangeTimelineTitle(zone, history)
                    },
                    data: zone.Id
                });
            })
        });

        return timelineEntries;
    }

    private buildSetpointChangeTimelineTitle(zone: IZoneWithProbes, history: ITemperatureSetpointHistory): string {
        let title: string = "";

        const zoneName = this.translationService.t("ReeferDetail.Zone") + " " + zone.Position;

        title += this.translationService.t("ReeferDetail.SetPointChange", zone.Name ? zone.Name : zoneName) + " ";

        title += this.translationService.t("ReeferDetail.To") + " ";

        title += this.unitConversionService.convertUnitWithSuffix(history.SetpointValue, UnitConversionType.Temperature);

        return title;
    }
}
